import React from "react";
import { Reveal, Eyebrow } from "./primitives";
import { CASE_NOTES } from "../../lib/siteData";
import { HeartHandshake, ShieldCheck } from "lucide-react";

const CAMP_INFO = [
  { k: "Frequency", v: "Monthly" },
  { k: "Focus", v: "Spine & neuro screening" },
  { k: "Cost", v: "Free of charge" },
  { k: "Referral", v: "Onward if needed" },
];

export const Community = () => (
  <section id="community" className="bg-[#E7ECEA] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="community-section">
    <div className="max-w-[1240px] mx-auto px-5 md:px-8">
      <div className="bg-white rounded-3xl border border-[#DCE3E0] p-8 md:p-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#4C7C68]/12 text-[#4C7C68] px-3.5 py-1.5 text-xs font-mono-ui uppercase tracking-wider">
            <HeartHandshake size={14} /> Community Initiative
          </span>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-6 leading-tight">
            Free monthly camps in villages near Bhubaneswar
          </h2>
          <div className="mt-6 space-y-4 text-[#161C22]/75 leading-relaxed">
            <p>
              Good spine and neurological care should not depend on how far you live from a
              city hospital. Each month I hold a free consultation camp in a village around
              Bhubaneswar, offering assessment, guidance and reassurance at no cost.
            </p>
            <p>
              Where something needs closer attention, people are helped along a clear
              referral pathway. The intent is education and early guidance — meeting
              communities where they are.
            </p>
          </div>

          <div className="mt-8 flex items-start gap-3 rounded-xl bg-[#4C7C68]/8 border border-[#4C7C68]/20 p-4">
            <ShieldCheck size={20} className="text-[#4C7C68] shrink-0 mt-0.5" />
            <p className="text-sm text-[#161C22]/75 leading-relaxed">
              <strong className="text-[#4C7C68]">Privacy first.</strong> Patient details from
              these camps are never shared, published or used for promotion — under any
              circumstances.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="grid grid-cols-2 gap-4" data-testid="camp-info-strip">
            {CAMP_INFO.map((c) => (
              <div key={c.k} className="rounded-2xl bg-[#F3F5F4] border border-[#DCE3E0] p-5">
                <div className="font-mono-ui text-[0.65rem] uppercase tracking-widest text-[#4C7C68]">{c.k}</div>
                <div className="font-display text-lg text-[#161C22] mt-2">{c.v}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const CaseNotes = () => (
  <section className="bg-[#14263D] py-24 md:py-32 relative overflow-hidden" data-testid="case-notes-section">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(50% 40% at 20% 20%, rgba(111,211,222,0.07), transparent 70%)" }}
    />
    <div className="relative max-w-[1240px] mx-auto px-5 md:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow tone="onDark">04 — Notes from consultations</Eyebrow>
        <h2 className="font-display font-light text-4xl md:text-5xl text-white mt-5 leading-tight">
          Illustrative, always anonymized
        </h2>
        <p className="text-[#F3F5F4]/60 mt-4 leading-relaxed">
          These short notes are composed to illustrate common situations. They contain no
          real names, photos or identifying details — and never will.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {CASE_NOTES.map((c, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <figure className="hover-lift h-full rounded-2xl border border-white/10 bg-[#1B3352] p-7 hover:border-[#5ECCA0]/40">
              <figcaption className="font-mono-ui text-[0.62rem] uppercase tracking-[0.2em] text-[#5ECCA0] mb-5">
                Anonymized Case Note
              </figcaption>
              <blockquote className="font-display italic text-lg text-[#F3F5F4]/85 leading-relaxed">
                “{c.a}”
              </blockquote>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
